import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import useAvailableSlots, { TimeSlot } from '../../api/useAvailableSlots';
import useScheduleMedicalCheck from '../../api/useScheduleMedicalCheck';
import useCancelMedicalCheck from '../../api/useCancelMedicalCheck';
import SlotPicker from './SlotPicker';
import { formatSlotWithZone, formatViewerEquivalent } from './SlotPicker/timeFormat';
import * as Style from './style.module.scss';

// HWM-22633: DrChrono / Polycare medical-check booking modal. Reuses the
// hero-storefront-app pill-picker UX — fetch /scheduling/available-slots/,
// pick a date + time pill, POST /scheduling/book-appointment/.
//
// [round 4d] `isReschedule={true}` opts the modal into the cancel-then-book
// chain so an existing DrChrono appointment can be replaced from the same
// surface. The backend's `_check_existing_appointment` guard refuses a
// fresh `book-appointment` while a prior appointment exists; cancelling
// first (with `is_reschedule=true` so the lead stays in MEDICAL_CHECK)
// unblocks the rebook.

interface ScheduleMedicalCheckModalProps {
    leadId: string;
    onClose: () => void;
    isReschedule?: boolean;
}

const ScheduleMedicalCheckModal: React.FC<ScheduleMedicalCheckModalProps> = ({
    leadId,
    onClose,
    isReschedule = false
}) => {
    const [selectedSlot, setSelectedSlot] = React.useState<{
        date: string;
        slot: TimeSlot;
    } | null>(null);

    const { data, isLoading, isError } = useAvailableSlots(leadId, true);
    const { mutateAsync: book, isPending: isBooking } = useScheduleMedicalCheck(onClose, {
        isReschedule
    });
    const { mutateAsync: cancel, isPending: isCancelling } = useCancelMedicalCheck();

    const slotsData = data?.data;
    const isWorking = isBooking || isCancelling;

    // HWM-23070: `clinic_timezone` is the canonical IANA name; `timezone` is
    // DrChrono's raw value and may be an alias like `US/Eastern`.
    const clinicTimezone = slotsData?.clinic_timezone || slotsData?.timezone || '';

    const handleSlotSelect = (date: string, slot: TimeSlot) => {
        setSelectedSlot({ date, slot });
    };

    // Null when the care manager is already on the clinic's clock.
    const viewerTime =
        selectedSlot?.slot.start_utc && clinicTimezone
            ? formatViewerEquivalent(selectedSlot.slot.start_utc, clinicTimezone)
            : null;

    // For reschedule: cancel(is_reschedule=true) then immediately book the
    // new slot. The mutateAsync hooks reject if the server returns 400 —
    // swallow here so the modal stays open with the slot selection intact
    // and the agent can retry. Per round-4d plan, the small window where
    // cancel succeeds but book fails leaves the lead in MEDICAL_CHECK
    // without an active appointment; the agent reruns the flow.
    const handleBook = async () => {
        if (!selectedSlot) return;
        const { date, slot } = selectedSlot;
        // Post the slot's own instant; the backend converts to clinic
        // wall-clock. The naive fallback covers an env still on an rtm-api
        // that predates `start_utc` and goes away with the legacy fields.
        const payload = slot.start_utc
            ? { lead_id: leadId, scheduled_time_utc: slot.start_utc }
            : { lead_id: leadId, scheduled_time: `${date}T${slot.time}:00` };
        try {
            if (isReschedule) {
                await cancel({ payload: { lead_id: leadId, is_reschedule: true } });
            }
            await book({ payload });
        } catch {
            /* surfaced via notification context */
        }
    };

    const title = isReschedule ? 'Reschedule medical check' : 'Schedule medical check';
    const primaryLabel = isReschedule
        ? isWorking
            ? 'Rescheduling…'
            : 'Reschedule'
        : isWorking
          ? 'Booking…'
          : 'Book';

    return (
        <Modal isCancelable onClose={onClose} className={Style.modal}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    {title}
                </H>

                {isLoading ? (
                    <P centered noDefaultMargin>
                        Loading available times…
                    </P>
                ) : null}

                {isError ? (
                    <P centered noDefaultMargin>
                        Could not load available times. Try again later.
                    </P>
                ) : null}

                {slotsData ? (
                    <SlotPicker
                        slots={slotsData.slots}
                        appointmentHours={slotsData.appointment_hours}
                        clinicTimezone={clinicTimezone}
                        firstAvailableDate={slotsData.first_available_date}
                        onSlotSelect={handleSlotSelect}
                        selectedSlot={
                            selectedSlot
                                ? { date: selectedSlot.date, time: selectedSlot.slot.time }
                                : null
                        }
                    />
                ) : null}

                {selectedSlot ? (
                    <P centered noDefaultMargin>
                        {`Selected: ${formatSlotWithZone(selectedSlot.slot, clinicTimezone)}`}
                        {viewerTime ? ` · your time ${viewerTime}` : ''}
                    </P>
                ) : null}

                <div className={Style.actionsCentered}>
                    <Button variant="outlineSecondary" onClick={onClose} disabled={isWorking}>
                        Cancel
                    </Button>
                    <Button onClick={handleBook} disabled={!selectedSlot || isWorking}>
                        {primaryLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ScheduleMedicalCheckModal;
